import { Suspense, lazy, useEffect } from 'react';

import { GAnalyticsScript } from '@/components/ga-analytics-script';
import { CandidateLoadingScreen } from '@/components/loading/candidate-loading-screen';
import { AuthProvider } from '@/context/AuthContext';
import { trackPageView } from '@/lib/analytics';
import { readUrlLocaleId } from '@/lib/url-locale';
import Home from '@/routes/Home';
import * as Sentry from '@sentry/react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router';
import { Toaster } from 'sonner';

import { CaptchaProvider } from '@kit/auth/captcha/client';

const Form = lazy(() => import('@/routes/Form'));
const Chat = lazy(() => import('@/routes/Chat'));

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as
  | string
  | undefined;

function RouteLoading() {
  return <CandidateLoadingScreen localeId={readUrlLocaleId(useLocation())} />;
}

function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    const url = [location.pathname, location.search].filter(Boolean).join('');
    trackPageView(url);
  }, [location.pathname, location.search]);

  return null;
}

export default function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <div className="flex min-h-screen items-center justify-center p-4 text-center">
          <div>
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="text-muted-foreground mt-2">
              Please try refreshing the page.
            </p>
          </div>
        </div>
      }
    >
      <CaptchaProvider>
        <AuthProvider>
          {GA_MEASUREMENT_ID && (
            <GAnalyticsScript measurementId={GA_MEASUREMENT_ID} />
          )}
          <BrowserRouter>
            <PageViewTracker />
            <Suspense fallback={<RouteLoading />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route
                  path="/:accountSlug/:engagementSlug"
                  element={<Form />}
                />
                <Route
                  path="/:accountSlug/:engagementSlug/chat"
                  element={<Chat />}
                />
                <Route path="/form/:token/:localeId" element={<Form />} />
                <Route path="/form/:token" element={<Form />} />
                <Route path="/chat/:token/:localeId" element={<Chat />} />
                <Route path="/chat/:token" element={<Chat />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </CaptchaProvider>
    </Sentry.ErrorBoundary>
  );
}
